import { useMemo, type ReactNode } from "react";

import clsx from "clsx";
import { isEqual, keyBy, omit, sum } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import {
  useChartState,
  usePageConfiguration,
  useSavedViews,
} from "@features/sales-v3/hooks";
import { useActiveTab } from "@features/sales-v3/hooks/use-active-tab";
import { type MetricFilters } from "@features/sales-v3/hooks/use-chart-state";
import {
  useTabState,
  type TabState,
} from "@features/sales-v3/hooks/use-tab-state";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import { useSplitView } from "@features/sales-v3/hooks/use-table-selection";
import { useTableState } from "@features/sales-v3/hooks/use-table-state";
import { type TabId } from "@features/sales-v3/tabs/components/tabs";
import { TAB_RANKINGS } from "@features/sales-v3/tabs/constants/tab";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { buildCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import {
  MetricCategorizer,
  MetricCategory,
} from "@features/sales-v3/utils/metric-categorizer";
import { SavedSearchOperatorLabels } from "@features/search-input/utils";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  MCAttributionWindowDays,
  NBAccountingMode,
  TimeGranularityV2,
} from "@north-beam/nb-common";
import { Modal } from "@shared/modal";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

const FILTER_OPERATOR_TO_HUMAN = {
  greaterThan: "Greater than",
  greaterThanEqualTo: "Greater than or equal to",
  lessThan: "Less than",
  lessThanOrEqualTo: "Less than or equal to",
  equalsTo: "Equal to",
} as const;

function tabSelectedCount(
  activeTab: TabId,
  tab: TabId,
  isSplitView: boolean,
  isBreakdownTab: boolean,
  tabState: TabState,
) {
  // if the active tab is higher ranked, then nothing is selected
  if (TAB_RANKINGS[activeTab] < TAB_RANKINGS[tab]) {
    return 0;
  }

  ////////// split view mode //////////////
  if (isSplitView) {
    if (isBreakdownTab) {
      // there are no ancestors for the breakdown tab
      const selectedCount =
        tabState.proposedFiltersState.inclusionAtomicities.length;
      return selectedCount;
    }
    const selectedCount = tabState.proposedFiltersState.inclusionAdKeys.length;

    return selectedCount;
  }

  ////////// aggregate view mode //////////

  // default select ALL mode

  if (tabState.selectAll) {
    const selectedCount =
      tabState.totalCount -
      (tabState.proposedFiltersState.exclusionAdKeys.length +
        sum(
          tabState.proposedFiltersState.exclusionAtomicities.map(
            (a) => a.count,
          ),
        )) +
      (tabState.proposedFiltersState.inclusionAdKeys.length +
        sum(
          tabState.proposedFiltersState.inclusionAtomicities.map(
            (a) => a.count,
          ),
        ));

    return selectedCount;
  }

  // default select NONE mode

  const selectedCount =
    tabState.proposedFiltersState.inclusionAdKeys.length +
    sum(
      tabState.proposedFiltersState.inclusionAtomicities.map((a) => a.count),
    ) +
    -(
      tabState.proposedFiltersState.exclusionAdKeys.length +
      sum(
        tabState.proposedFiltersState.exclusionAtomicities.map((a) => a.count),
      )
    );

  return selectedCount;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  columnConfigs: ColumnDef[];
  metrics: SalesPageMetric[];
}

export const ConfigurationDetailsModal = ({
  columnConfigs,
  metrics,
  isOpen,
  onClose,
}: Props) => {
  const { isDemoMode } = usePrefs();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const pageConfig = usePageConfiguration();
  const { selectedSavedView, isStateSaved } = useSavedViews();
  const { chartConfigs } = useChartState();
  const { tableState } = useTableState();
  const { user } = useUser();
  const { tabState: breakdownTabState } = useTabState("breakdown");
  const { tabState: campaignTabState } = useTabState("campaign");
  const { tabState: adsetTabState } = useTabState("adset");
  const { tabState: adTabState } = useTabState("ad");
  const { activeTab } = useActiveTab();
  const isSplitView = useSplitView();
  const attributionMethodEnum = user.attributionMethodEnum;
  const getCompareQueryMetadata = buildCompareQueryMetadata({
    metrics: [],
    attributionMethodEnum,
    compareDateRange: pageConfig.compareDateRange,
  });
  const compareDateRangeInterval = pageConfig.compareDateRange
    ? deriveComparisonDateRange(
        pageConfig.dateRangeInterval,
        pageConfig.compareDateRange,
        user.timezone,
        pageConfig.timeGranularity,
      )
    : null;

  const metricsById = useMemo(() => {
    return new Map(metrics.map((e) => [e.id, e.name]));
  }, [metrics]);

  const metricsByCategory = useMemo(() => {
    const metricCategories = new MetricCategorizer(metrics);
    const metricCategoryNames = Object.values(MetricCategory).filter(
      (category) => category !== MetricCategory.ALL,
    );

    const rv = [];
    const columnConfigsById = keyBy(columnConfigs, "id");

    for (const category of metricCategoryNames) {
      const categoryMetrics = metricCategories.getMetricsByCategory(category);
      const categoryMetricIds = categoryMetrics.map((e) => e.id);
      const includedMetrics = [];

      for (const config of tableState.columnConfigs) {
        if (categoryMetricIds.includes(config.id)) {
          const columnConfig = columnConfigsById[config.id];
          includedMetrics.push(columnConfig.title);
        }
      }

      if (includedMetrics.length > 0) {
        rv.push({ category, metrics: includedMetrics });
      }
    }

    return rv;
  }, [tableState.columnConfigs, columnConfigs, metrics]);

  const breakdownTabSelectedCount = useMemo(
    () =>
      tabSelectedCount(
        activeTab,
        "breakdown",
        isSplitView,
        true,
        breakdownTabState,
      ),
    [isSplitView, activeTab, breakdownTabState],
  );

  const campaignTabSelectedCount = useMemo(
    () =>
      tabSelectedCount(
        activeTab,
        "campaign",
        isSplitView,
        false,
        campaignTabState,
      ),
    [isSplitView, activeTab, campaignTabState],
  );

  const adsetTabSelectedCount = useMemo(
    () =>
      tabSelectedCount(activeTab, "adset", isSplitView, false, adsetTabState),
    [isSplitView, activeTab, adsetTabState],
  );

  const adTabSelectedCount = useMemo(
    () => tabSelectedCount(activeTab, "ad", isSplitView, false, adTabState),
    [isSplitView, activeTab, adTabState],
  );

  const changedPageConfigs = useMemo(() => {
    const rv: Set<keyof typeof pageConfig.pageConfiguration> = new Set();

    if (!selectedSavedView) {
      return rv;
    }

    // breakdowns
    if (
      !isEqual(
        pageConfig.pageConfiguration.breakdowns.map((i) => i.key),
        selectedSavedView.config.pageConfiguration.breakdowns.map((i) => i.key),
      )
    ) {
      rv.add("breakdowns");
    }

    for (const [key, value] of Object.entries(pageConfig.pageConfiguration)) {
      let isChanged: boolean;
      // do this one manually
      if (key === "breakdowns") {
        continue;
      } else {
        isChanged = !isEqual(
          value,
          selectedSavedView.config.pageConfiguration[
            key as keyof typeof pageConfig.pageConfiguration
          ],
        );
      }

      if (isChanged) {
        rv.add(key as keyof typeof pageConfig.pageConfiguration);
      }
    }

    return rv;
  }, [pageConfig, selectedSavedView]);

  const changedTableState = useMemo(() => {
    const rv: Set<keyof typeof tableState> = new Set();

    if (!selectedSavedView) {
      return rv;
    }

    for (const [key, value] of Object.entries(tableState)) {
      const savedValue =
        selectedSavedView.config.tableState[key as keyof typeof tableState];
      const isChanged = !isEqual(
        value,
        Array.isArray(savedValue)
          ? savedValue.map((i) => omit(i, "name"))
          : savedValue,
      );

      if (isChanged) {
        rv.add(key as keyof typeof tableState);
      }
    }

    return rv;
  }, [tableState, selectedSavedView]);

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div className="font-bold text-xl">Configuration details</div>
      }
    >
      <div className="flex flex-col text-[1.1rem]">
        <Row>
          <Label>State saved?</Label>
          <Value>
            <span
              className={clsx("border rounded text-sm px-2.5 py-0.5", {
                "bg-sun-2 border-sun+2 text-[#996800]": !isStateSaved,
                "bg-alien-2 border-alien+2 text-[#3B8139]": isStateSaved,
              })}
            >
              {isStateSaved ? "Yes" : "No"}
            </span>
          </Value>
        </Row>
        <Row>
          <Label>Saved view</Label>
          <Value>
            {generateAnonBase64Hash({
              value: selectedSavedView?.name,
              prefix: "quick-filter",
              doAnon: isDemoMode && !selectedSavedView?.is_template,
            })}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("breakdowns")}>
            Breakdowns
          </Label>
          <Value>
            {pageConfig.breakdowns.length === 0 && "-"}
            {pageConfig.breakdowns.map((e) => (
              <div key={e.key}>{e.key}</div>
            ))}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("filters")}>
            Page filters
          </Label>
          <Value>
            {pageConfig.filters.length === 0 && "-"}
            {pageConfig.filters.map((e) => {
              const config = breakdownConfigs.find((v) => v.key === e.key);

              return (
                <div key={e.key}>
                  <div className="mb-2">
                    {e.key} ({e.values.length} of {config?.choices.length})
                  </div>
                  {e.values.map((v) => (
                    <SubValue key={v}>{v}</SubValue>
                  ))}
                </div>
              );
            })}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("attributionModel")}>
            Attribution Model
          </Label>
          <Value>
            {attributionMethodEnum.getLabel(pageConfig.attributionModel)}
          </Value>
        </Row>
        {pageConfig.accountingMode === "accrual" && (
          <Row>
            <Label showIndicator={changedPageConfigs.has("attributionWindow")}>
              Attribution Window
            </Label>
            <Value>
              {MCAttributionWindowDays.getLabel(pageConfig.attributionWindow)}
            </Value>
          </Row>
        )}
        <Row>
          <Label showIndicator={changedPageConfigs.has("accountingMode")}>
            Accounting Mode
          </Label>
          <Value>{NBAccountingMode.getLabel(pageConfig.accountingMode)}</Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("timeGranularity")}>
            Time granularity
          </Label>
          <Value>
            {TimeGranularityV2.getLabel(pageConfig.timeGranularity)}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("dateRange")}>
            Time period
          </Label>
          <Value>{formatDateRange(pageConfig.dateRangeInterval)}</Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("compareDateRange")}>
            Compare time period
          </Label>
          <Value>
            {compareDateRangeInterval
              ? formatDateRange(compareDateRangeInterval)
              : "-"}
          </Value>
        </Row>
        {chartConfigs.map((chartConfig, i) => {
          const savedChartConfig =
            selectedSavedView?.config.chartState.chartConfigs[i];
          const isChanged = !isEqual(
            omit(savedChartConfig, "alignAxesScale"),
            omit(chartConfig, "alignAxesScale"),
          );

          return (
            <div key={i}>
              <Row>
                <Label showIndicator={isChanged}>Chart {i + 1}, Metric</Label>
                <Value>
                  <span className="capitalize">
                    {metricsById.get(chartConfig.mainMetric.metricName) ||
                      chartConfig.mainMetric.metricName}
                  </span>
                </Value>
              </Row>
              <Row>
                <Label showIndicator={isChanged}>Chart {i + 1}, Filters</Label>
                <ChartFilters filters={chartConfig.mainMetric.filters} />
              </Row>
              {chartConfig.comparisons.map((compare, j) => {
                const metadata = getCompareQueryMetadata(compare);

                return (
                  <div key={j}>
                    <Row>
                      <Label showIndicator={isChanged}>
                        Chart {i + 1}, Comparison {j + 1}
                      </Label>
                      <Value>
                        {metadata.label}:{" "}
                        {metadata.optionLabel || compare.queryValue}
                      </Value>
                    </Row>
                    <Row>
                      <Label showIndicator={isChanged}>
                        Chart {i + 1}, Comparison {j + 1}, Filters
                      </Label>
                      <ChartFilters filters={compare.filters} />
                    </Row>
                  </div>
                );
              })}
            </div>
          );
        })}
        <Row>
          <Label showIndicator={changedPageConfigs.has("searchQuery")}>
            Search query
          </Label>
          <Value>
            {pageConfig.searchQuery.query ? (
              <>
                <span className="capitalize">
                  {pageConfig.searchQuery.method}
                </span>
                {pageConfig.searchQuery.operator && (
                  <span className="capitalize">
                    ,{" "}
                    {SavedSearchOperatorLabels[pageConfig.searchQuery.operator]}
                  </span>
                )}
                : {pageConfig.searchQuery.query}
              </>
            ) : (
              "-"
            )}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedPageConfigs.has("activeTab")}>
            Active Tab
          </Label>
          <Value>
            {!pageConfig.breakdowns.length ||
            (pageConfig.breakdowns.length === 1 &&
              pageConfig.breakdowns[0].key === "Platform (Northbeam)") ? (
              "Platform"
            ) : (
              <span className="capitalize">{pageConfig.activeTab}</span>
            )}
          </Value>
        </Row>
        <Row>
          <Label
            showIndicator={
              changedPageConfigs.has("sorting") ||
              changedPageConfigs.has("sortingOrder")
            }
          >
            Table sort
          </Label>
          <Value>
            <span className="capitalize">{pageConfig.sorting?.id}</span>:{" "}
            <span className="uppercase">{pageConfig.sortingOrder}</span>
          </Value>
        </Row>
        <Row>
          <Label
            showIndicator={
              changedPageConfigs.has("statuses") ||
              changedPageConfigs.has("metricFilters")
            }
          >
            Table filters
          </Label>
          <Value>
            {(!pageConfig.statuses || pageConfig.statuses.length === 0) &&
              (pageConfig.metricFilters.metricClauses || []).length === 0 &&
              "-"}
            {pageConfig.statuses && pageConfig.statuses.length > 0 && (
              <>
                Status:{" "}
                {(pageConfig.statuses || []).map((e, i) => (
                  <span key={e}>
                    <span className="capitalize">{e}</span>
                    {i + 1 < (pageConfig.statuses || []).length && ", "}
                  </span>
                ))}
                <br />
              </>
            )}
            {(pageConfig.metricFilters.metricClauses || []).map((e) => (
              <div key={e.metric.id}>
                <span className="capitalize">
                  {metricsById.get(e.metric.id) || e.metric.id}:
                </span>{" "}
                <span>
                  {FILTER_OPERATOR_TO_HUMAN[e.operator]} {e.value}
                </span>
                <br />
              </div>
            ))}
          </Value>
        </Row>
        <Row>
          <Label showIndicator={changedTableState.has("columnConfigs")}>
            Table metrics
          </Label>
          <Value>
            {metricsByCategory.map((e) => (
              <div key={e.category}>
                <div className="mb-2">
                  ({e.metrics.length}) {e.category}
                </div>
                {e.metrics.map((v) => (
                  <SubValue key={v}>{v}</SubValue>
                ))}
              </div>
            ))}
          </Value>
        </Row>
        <Row>
          <Label>Table selection</Label>
          <Value>
            Breakdown tab: {breakdownTabSelectedCount} selected
            <br />
            Campaign tab: {campaignTabSelectedCount} selected
            <br />
            Adset tab: {adsetTabSelectedCount} selected
            <br />
            Ad tab: {adTabSelectedCount} selected
          </Value>
        </Row>
      </div>
    </Modal>
  );
};

const Row = ({ children }: { children: ReactNode }) => (
  <div className="flex mb-8">{children}</div>
);

const Label = ({
  showIndicator = false,
  children,
}: {
  showIndicator?: boolean;
  children: ReactNode;
}) => (
  <div className="shrink-0 w-[200px] pr-4 font-light text-interstellar-1">
    <span className="relative">
      {children}
      {showIndicator && (
        <span
          className={clsx(
            "inline-block rounded-full w-[0.5rem] h-[0.5rem] bg-mars-1",
            "absolute right-0 bottom-0 -mr-2 mb-3",
          )}
        ></span>
      )}
    </span>
  </div>
);

const Value = ({ children }: { children: ReactNode }) => <div>{children}</div>;

const SubValue = ({ children }: { children: ReactNode }) => (
  <div className="text-sm text-interstellar-1 font-light mb-2">{children}</div>
);

const ChartFilters = ({ filters }: { filters: MetricFilters[] }) => {
  if (!filters || filters.length === 0) {
    return <Value>-</Value>;
  }

  return (
    <Value>
      {filters.map((filter) => (
        <div key={filter.breakdown.key}>
          <div className="mb-2">
            {filter.breakdown.key} ({filter.values.length} of{" "}
            {filter.breakdown.choices.filter((e) => e.value !== "").length})
          </div>
          {filter.values.map((v) => (
            <SubValue key={v.value}>{v.label}</SubValue>
          ))}
        </div>
      ))}
    </Value>
  );
};
